import * as React from "react";
import { AppBar, Box, Toolbar, Container, CircularProgress } from "@mui/material";
import axios from "axios";
import fds from "../../Images/fdc7.png";

function Appbar() {
  const [serverStatus, setServerStatus] = React.useState({
    isOnline: false,
    isChecking: true,
    lastChecked: null,
  });

  const checkServerStatus = React.useCallback(async () => {
    try {
      // Check if axios has a baseURL configured
      const baseURL = axios.defaults.baseURL || window.location.origin;
      const response = await axios.get('/', {
        timeout: 5000,
      });
      
      setServerStatus({
        isOnline: response.status === 200,
        isChecking: false,
        lastChecked: new Date(),
      });
    } catch (error) {
      console.error("Server health check failed:", error);
      setServerStatus({
        isOnline: false,
        isChecking: false,
        lastChecked: new Date(),
      });
    }
  }, []);

  // Check server status on mount
  React.useEffect(() => {
    checkServerStatus();
    
    // Optional: Set up periodic health checks (every 30 seconds)
    const interval = setInterval(checkServerStatus, 30000);
    
    return () => clearInterval(interval);
  }, [checkServerStatus]);

  // Determine status display
  const getStatusConfig = () => {
    if (serverStatus.isChecking) {
      return {
        color: "#FFA500",
        text: "Checking Server...",
        shadow: "#FFA500",
        showPulse: false,
      };
    }
    
    if (serverStatus.isOnline) {
      return {
        color: "#00ff88",
        text: "System Online",
        shadow: "#00ff88",
        showPulse: true,
      };
    }
    
    return {
      color: "#ff4444",
      text: "System Offline",
      shadow: "#ff4444",
      showPulse: true,
    };
  };

  const statusConfig = getStatusConfig();

  return (
    <Box>
      <AppBar
        position="fixed" 
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.03)", 
          backdropFilter: "blur(16px) saturate(180%)", 
          webkitBackdropFilter: "blur(16px) saturate(180%)", 
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)", 
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)", 
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{ height: 70, justifyContent: "space-between" }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={fds}
              alt="Logo"
              sx={{
                height: { xs: 40, md: 50 },
                width: "auto",
              }}
            />

            {/* Status Indicator */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: "rgba(255, 255, 255, 0.05)",
                padding: "6px 14px",
                borderRadius: "20px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.08)",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                }
              }}
              onClick={checkServerStatus}
              title="Click to refresh server status"
            >
              {serverStatus.isChecking ? (
                <CircularProgress 
                  size={10} 
                  sx={{ 
                    color: statusConfig.color,
                  }} 
                />
              ) : (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: statusConfig.color,
                    borderRadius: "50%",
                    boxShadow: `0 0 12px ${statusConfig.shadow}`,
                    animation: statusConfig.showPulse ? "pulse 2s infinite" : "none",
                  }}
                />
              )}
              <Box
                component="span"
                sx={{
                  fontSize: "0.8rem",
                  color: "rgba(255, 255, 255, 0.7)",
                  fontWeight: 500,
                }}
              >
                {statusConfig.text}
              </Box>
            </Box>
          </Toolbar>
        </Container>

        {/* CSS for the pulse animation */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @keyframes pulse {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 136, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(0, 255, 136, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 136, 0); }
          }
        `,
          }}
        />
      </AppBar>
      {/* Spacer so content doesn't hide under the fixed AppBar */}
      <Toolbar />
    </Box>
  );
}

export default Appbar;